import React from "react";
import { CV } from "../components/cv";
import AlexanderMImg from "@/assets/images/alexander-m.png";

export default function CVAlexanderMadsen() {
  React.useEffect(() => {
    document.title = "BoldBrave CV - Alexander Madsen";
  }, []);

  return (
    <CV
      name="Alexander Madsen"
      photo={AlexanderMImg}
      title="Senior Frontend Developer"
      subtitle="20+ years of UI design and web development — React and TypeScript, design systems and accessibility in large, complex organisations."
      summary="Senior frontend developer with more than 20 years of experience in UI design, web development and software engineering, building modern, user-centric web applications across a wide range of industries with HTML, CSS, JavaScript, React and TypeScript. A significant part of the career has been spent in large, complex organisations including IKEA, where he played a key role in developing the SKAPA digital design system for Ingka and Inter IKEA — reusable components with strong emphasis on accessibility, usability and WCAG compliance, supporting both internal platforms and external franchise teams. In recent assignments he has taken on more senior responsibilities, working closely with backend developers, UX designers and product teams and contributing to planning, refinement and delivery. Known for a structured approach, independence and a collaborative mindset. Uses AI-assisted development tools pragmatically to improve speed and quality while retaining full ownership of architecture and code. Works fluently in Danish, Swedish and English."
      skills={{
        Development: [
          "JavaScript",
          "TypeScript",
          "React",
          "Node.js",
          "Next.js",
          "Vue.js",
          "HTML",
          "CSS",
          "Mobile-first development",
        ],
        "AI-Assisted Development": [
          "Cursor",
          "Claude Code",
          "GitHub Copilot",
          "ChatGPT",
          "Prompt engineering",
          "AI-supported refactoring, testing & code review",
        ],
        "UI & Design Systems": [
          "Design systems",
          "Figma",
          "Design-to-code workflow",
          "Storybook",
          "Material UI",
          "Accessibility (WCAG)",
          "Component libraries",
        ],
        "Cloud & DevOps": [
          "CI/CD",
          "Docker",
          "Azure",
          "GitHub Actions",
          "GitLab CI",
        ],
        "APIs & Integration": [
          "GraphQL",
          "Apollo",
          "REST APIs",
          "Swagger / OpenAPI",
        ],
        "Testing & Quality": [
          "Jest",
          "Cypress",
          "React Testing Library",
          "Frontend test automation",
        ],
        "Tools & Platforms": [
          "Optimizely / EPiServer",
          "Git",
          "GitHub",
          "GitLab",
          "Azure DevOps",
          "Jira",
          "Linux",
        ],
        Languages: ["Danish", "Swedish", "English"],
      }}
      experience={[
        {
          role: "Senior Frontend Developer / Frontend Tech Lead",
          company: "Skanska, Malmö",
          period: "2024 – Present",
          description:
            "Digital platforms supporting both internal operations and customer engagement at a global construction and project development leader.",
          highlights: [
            "Part of the architectural transition from a legacy codebase (Optimizely 11 to 12, jQuery) to modern frontend technologies within the CMS.",
            "Some backend development in .NET/C#, including debugging and enhancing controllers and models in an MVC architecture.",
            "Used AI-assisted tools (Cursor, Claude Code, GitHub Copilot, ChatGPT) daily — refactoring legacy Optimizely/jQuery code, navigating unfamiliar .NET/C# territory, generating tests and accelerating code review — while retaining full ownership of architecture and code quality.",
            "Defined frontend coding standards and reusable patterns for scalable development.",
            "Worked directly from Figma designs with UX, reviewing specs and prototypes, giving feedback on technical feasibility and translating designs into accessible, reusable components.",
            "Guided technical decisions, contributed to sprint planning and refinement, and supported release cycles and performance tuning.",
          ],
        },
        {
          role: "Senior Frontend Developer",
          company: "Totalkredit, Copenhagen",
          period: "2024",
          description:
            "Short assignment in the Danish financial sector, focused on an energy calculator aimed at improving household energy efficiency.",
          highlights: [
            "Developed frontend functionality in React and supported UI-related tasks and usability improvements.",
            "Coached and mentored junior developers and contributed to knowledge sharing and frontend best practices.",
          ],
        },
        {
          role: "User Interface Developer / Software Developer",
          company: "ESS, Lund",
          period: "2022 – 2023",
          description:
            "Building new web applications from scratch for advanced scientific infrastructure and control systems.",
          highlights: [
            "Developed multiple web applications used to control and interact with hardware devices.",
            "Built frontend solutions for systems collecting data from sensors and readers.",
            "Worked with modern frontend architectures and micro-frontend patterns. React, Node.js, Docker, Material UI, Storybook, Cypress, GitLab, OpenAPI.",
          ],
        },
        {
          role: "Software Developer",
          company: "IKEA, Malmö",
          period: "2019 – 2022",
          description:
            "Worked on the SKAPA digital design system used by internal teams and external franchise developers.",
          highlights: [
            "Developed reusable frontend components with focus on accessibility, usability and WCAG compliance.",
            "Worked closely with designers in Figma to keep the SKAPA component library aligned between design and code, ensuring React and Vue implementations matched the design source of truth across tokens, variants and states.",
            "Supported internal development teams through reusable components, documentation and frontend standards, and contributed to CI/CD pipelines and frontend platform stability.",
            "React, TypeScript, Vue.js, Preact, Storybook, Lerna, SASS, GitHub Actions.",
          ],
        },
        {
          role: "Software Developer",
          company: "Tunstall, Malmö",
          period: "2019",
          description:
            "Digital healthcare and safety solutions for telecare, assisted living and remote monitoring, where reliability and accessibility are safety-critical.",
          highlights: [
            "Developed new frontend features and maintained existing functionality, and implemented unit tests to ensure stability and safety.",
            "React, TypeScript, GraphQL, Apollo, Jest, Redux, Material UI, Azure.",
          ],
        },
        {
          role: "Web Developer",
          company: "Playtech / Mobenga, Malmö",
          period: "2018 – 2019",
          description:
            "Mobile-first platforms for sportsbooks and gaming operators — high-traffic, real-time systems with strong demands on performance and cross-device consistency.",
          highlights: [
            "Developed new frontend features in React and migrated legacy Knockout.js code to React.",
            "Worked on both mobile and desktop web applications and maintained legacy solutions.",
          ],
        },
        {
          role: "Software Developer / Web Developer",
          company:
            "Malmö Kyrkogårdsförvaltningen, Aller Media & Human IT, Malmö",
          period: "2017",
          description:
            "Parallel assignments building and maintaining WordPress-based public and editorial web platforms.",
          highlights: [
            "Built and launched the official website for Malmö Kyrkogårdsförvaltningen, developing and adapting themes, plugins and custom functionality.",
            "Maintained and further developed Aller Media's high-traffic WordPress sites in agile scrum teams.",
            "Developed and launched humanit.se together with the in-house team.",
          ],
        },
        {
          role: "Web Developer",
          company: "Civil Rights Defenders, Malmö",
          period: "2017",
          description:
            "Transparency and data-driven communication around reported human rights violations.",
          highlights: [
            "Developed a data-driven website visualising human rights violations, with interactive search, map and data visualisations in Vue.js.",
          ],
        },
        {
          role: "Web Developer / Webmaster",
          company: "Ekstra Bladet, Copenhagen",
          period: "2006 – 2017",
          description:
            "One of Scandinavia's largest online newspapers, with high-traffic digital platforms and continuous content publishing.",
          highlights: [
            "Developed new frontend features, products and digital concepts, and designed templates and content marketing systems.",
            "Maintained and refactored large JavaScript and JSP codebases, and supported and trained end users in CMS usage.",
          ],
        },
      ]}
    />
  );
}
